import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Database, 
  Table, 
  ChevronRight, 
  ChevronDown, 
  Loader2, 
  Search,
  RefreshCw,
  Package,
  Cpu,
  FileText,
  Settings,
  Layers,
  Eye,
  Copy,
  Check,
  X,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Columns,
  LayoutGrid,
  List,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Link2,
  Hash,
  Calendar,
  ToggleLeft,
  Type,
  Braces,
  ListTree,
  FileJson,
  Info,
  ExternalLink,
  CheckSquare,
  Square,
  BarChart3,
  TrendingUp,
  Clock,
  Activity,
  GitBranch,
  User,
  Globe,
  Zap,
  MousePointerClick,
  FileEdit,
  Trash2,
  PlusCircle,
  History,
  AlertCircle,
  HardDrive
} from 'lucide-react';
import api from '../services/api';
import { formatIST, formatTimeAgoIST, formatISTCompact } from '../utils/timezone';
import BackupsView from '../components/database/BackupsView';

/**
 * Database Explorer Page - Enterprise Grade
 * Professional database viewer with data-aware visualization
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */

// ============================================
// TYPE-AWARE VALUE COMPONENTS
// ============================================

const TYPE_COLORS = {
  string: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  number: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  boolean: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  date: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  objectId: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  array: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  object: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  null: { bg: 'bg-slate-50', text: 'text-slate-400', border: 'border-slate-200' },
  email: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  url: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
};

const TYPE_ICONS = {
  string: Type,
  number: Hash,
  boolean: ToggleLeft,
  date: Calendar,
  objectId: Link2,
  array: ListTree,
  object: Braces,
  null: X,
  email: Type,
  url: ExternalLink,
};

const detectType = (value) => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return 'date';
    if (/^[a-f0-9]{24}$/.test(value)) return 'objectId';
    if (/^[\w.-]+@[\w.-]+\.\w+$/.test(value)) return 'email';
    if (value.startsWith('http://') || value.startsWith('https://')) return 'url';
  }
  return 'string';
};

// Type Badge Component
const TypeBadge = ({ type, small = false }) => {
  const colors = TYPE_COLORS[type] || TYPE_COLORS.string;
  const Icon = TYPE_ICONS[type] || Type;
  
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${colors.bg} ${colors.text}`}>
      <Icon className={small ? "w-2.5 h-2.5" : "w-3 h-3"} />
      {!small && type}
    </span>
  );
};

// Smart Cell Value Component
const CellValue = ({ value, fieldName, onClickId, onCopy }) => {
  const [copied, setCopied] = useState(false);
  const type = detectType(value);
  const colors = TYPE_COLORS[type] || TYPE_COLORS.string;
  
  const handleCopy = (e) => {
    e.stopPropagation();
    const textToCopy = typeof value === 'object' ? JSON.stringify(value) : String(value);
    try {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.log('Copy failed:', err);
    }
  };
  
  if (value === null || value === undefined) {
    return <span className="text-slate-300 italic text-xs">null</span>;
  }
  
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'}`} />
        {value ? 'true' : 'false'}
      </span>
    );
  }
  
  if (typeof value === 'number') {
    return (
      <span className="font-mono text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
        {value.toLocaleString()}
      </span>
    );
  }
  
  if (Array.isArray(value)) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); }}
        className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded text-xs font-medium hover:bg-cyan-100 transition-colors"
      >
        <ListTree className="w-3 h-3" />
        [{value.length}]
      </button>
    );
  }
  
  if (typeof value === 'object') {
    const keys = Object.keys(value).length;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); }}
        className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium hover:bg-indigo-100 transition-colors"
      >
        <Braces className="w-3 h-3" />
        {`{${keys}}`}
      </button>
    );
  }
  
  // String values
  const strValue = String(value);
  
  // ObjectId - clickable link
  if (type === 'objectId') {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onClickId && onClickId(value); }}
        className="inline-flex items-center gap-1 font-mono text-xs text-rose-600 hover:text-rose-800 hover:underline group"
        title={`Click to explore: ${value}`}
      >
        <Link2 className="w-3 h-3 opacity-50 group-hover:opacity-100" />
        {strValue.substring(0, 8)}...
      </button>
    );
  }
  
  // Date
  if (type === 'date') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-purple-600" title={value}>
        <Calendar className="w-3 h-3 opacity-50" />
        {formatISTCompact(value)}
      </span>
    );
  }
  
  // URL
  if (type === 'url') {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 hover:underline"
      >
        <ExternalLink className="w-3 h-3" />
        {strValue.substring(0, 30)}...
      </a>
    );
  }
  
  // Regular string
  const truncated = strValue.length > 40;
  return (
    <span 
      className="text-xs text-slate-700 group relative cursor-pointer"
      title={truncated ? strValue : undefined}
      onClick={handleCopy}
    >
      {truncated ? strValue.substring(0, 40) + '...' : strValue}
      <span className={`absolute -top-6 left-0 px-1.5 py-0.5 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity ${copied ? 'bg-green-600' : ''}`}>
        {copied ? 'Copied!' : 'Click to copy'}
      </span>
    </span>
  );
};

// ============================================
// FIELD STATISTICS TOOLTIP
// ============================================

const FieldStatsTooltip = ({ stats, fieldName }) => {
  if (!stats) return null;
  
  return (
    <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-64 text-xs">
      <div className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
        <TypeBadge type={stats.type} />
        {fieldName}
      </div>
      
      <div className="space-y-1.5">
        <div className="flex justify-between">
          <span className="text-slate-500">Unique values:</span>
          <span className="font-medium">{stats.unique_count?.toLocaleString() || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Null count:</span>
          <span className="font-medium">{stats.null_count} ({stats.null_percentage}%)</span>
        </div>
        
        {stats.type === 'number' && (
          <>
            <div className="flex justify-between">
              <span className="text-slate-500">Min / Max:</span>
              <span className="font-medium font-mono">{stats.min} / {stats.max}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Average:</span>
              <span className="font-medium font-mono">{stats.avg}</span>
            </div>
          </>
        )}
        
        {stats.type === 'string' && stats.avg_length && (
          <div className="flex justify-between">
            <span className="text-slate-500">Avg length:</span>
            <span className="font-medium">{stats.avg_length} chars</span>
          </div>
        )}
        
        {stats.sample_values?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="text-slate-500 mb-1">Sample values:</div>
            <div className="flex flex-wrap gap-1">
              {stats.sample_values.slice(0, 5).map((v, i) => (
                <span key={i} className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] truncate max-w-[100px]">
                  {v}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// DOCUMENT DETAIL MODAL
// ============================================

const DocumentModal = ({ document, onClose, collectionName, onNavigate }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(JSON.stringify(document, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.log('Copy failed:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Document Details</h3>
            <p className="text-sm text-slate-500">{collectionName} • {Object.keys(document).length} fields</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy JSON'}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
        
        {/* Modal Body - Scrollable */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-2">
            {Object.entries(document).map(([key, value]) => {
              const type = detectType(value);
              const colors = TYPE_COLORS[type];
              
              return (
                <div key={key} className={`border rounded-lg overflow-hidden ${colors.border}`}>
                  <div className={`flex items-center justify-between px-4 py-2 ${colors.bg}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-slate-700">{key}</span>
                      <TypeBadge type={type} small />
                    </div>
                    {type === 'objectId' && (
                      <button
                        onClick={() => onNavigate && onNavigate(value)}
                        className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Explore
                      </button>
                    )}
                  </div>
                  <div className="px-4 py-3 bg-white">
                    <DocumentValue value={value} onNavigate={onNavigate} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Render document value with proper formatting
const DocumentValue = ({ value, onNavigate }) => {
  const type = detectType(value);
  
  if (value === null || value === undefined) {
    return <span className="text-slate-400 italic">null</span>;
  }
  
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {value.toString()}
      </span>
    );
  }
  
  if (typeof value === 'number') {
    return <span className="font-mono text-blue-600 text-lg">{value.toLocaleString()}</span>;
  }
  
  if (type === 'date') {
    return (
      <div>
        <span className="text-purple-600 font-medium">{formatIST(value)}</span>
        <span className="text-slate-400 text-sm ml-2">({value})</span>
      </div>
    );
  }
  
  if (type === 'objectId') {
    return (
      <button
        onClick={() => onNavigate && onNavigate(value)}
        className="font-mono text-rose-600 hover:text-rose-800 hover:underline flex items-center gap-2"
      >
        <Link2 className="w-4 h-4" />
        {value}
      </button>
    );
  }
  
  if (type === 'url') {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sky-600 hover:text-sky-800 hover:underline flex items-center gap-2 break-all"
      >
        <ExternalLink className="w-4 h-4 flex-shrink-0" />
        {value}
      </a>
    );
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-slate-400 italic">empty array</span>;
    }
    return (
      <div className="bg-slate-50 rounded-lg p-3 max-h-48 overflow-auto">
        <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
      </div>
    );
  }
  
  if (typeof value === 'object') {
    return (
      <div className="bg-slate-50 rounded-lg p-3 max-h-48 overflow-auto">
        <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
      </div>
    );
  }
  
  // Long strings
  if (typeof value === 'string' && value.length > 100) {
    return (
      <div className="text-slate-700 whitespace-pre-wrap break-words bg-slate-50 p-3 rounded-lg text-sm max-h-48 overflow-auto">
        {value}
      </div>
    );
  }
  
  return <span className="text-slate-700">{String(value)}</span>;
};

// ============================================
// DOCUMENT HISTORY MODAL COMPONENT
// ============================================

const DocumentHistoryModal = ({ document, collectionName, onClose }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [error, setError] = useState(null);

  // Get document ID - try various common ID fields
  const documentId = document?._id || document?.id || document?.hpn_compact || document?.event_id;

  useEffect(() => {
    loadHistory();
  }, [documentId, collectionName]);

  const loadHistory = async () => {
    if (!documentId) {
      setError('Cannot determine document ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/provenance/document/${collectionName}/${documentId}`);
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to load document history:', err);
      setError(err.response?.status === 404 ? 'No history found for this document' : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const toggleEvent = (eventId) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  // Use IST timezone utilities
  const formatTimestamp = (ts) => formatIST(ts);
  const formatTimeAgo = (ts) => formatTimeAgoIST(ts);

  const HISTORY_OP_COLORS = {
    INSERT: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'bg-emerald-500' },
    UPDATE: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'bg-blue-500' },
    DELETE: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'bg-red-500' },
  };

  const HISTORY_OP_ICONS = {
    INSERT: PlusCircle,
    UPDATE: FileEdit,
    DELETE: Trash2,
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <History className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Document History</h3>
              <p className="text-sm text-slate-500">
                {collectionName} • <code className="bg-slate-100 px-1 rounded text-xs">{String(documentId).slice(0, 20)}{String(documentId).length > 20 ? '...' : ''}</code>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        {/* Modal Body */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
              <p className="text-sm text-slate-500">Loading history...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-10 h-10 text-amber-500 mb-3" />
              <p className="text-sm text-slate-600">{error}</p>
              <p className="text-xs text-slate-400 mt-1">This document may not have been modified after provenance tracking was enabled.</p>
            </div>
          ) : history?.history?.length > 0 ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-500 mb-1">Created</div>
                  <div className="text-sm font-medium text-slate-700">{formatTimestamp(history.created_at)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">by {history.created_by || 'Unknown'}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-500 mb-1">Last Modified</div>
                  <div className="text-sm font-medium text-slate-700">{formatTimestamp(history.last_modified_at)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">by {history.last_modified_by || 'Unknown'}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-500 mb-1">Total Changes</div>
                  <div className="text-2xl font-bold text-indigo-600">{history.total_events}</div>
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <GitBranch className="w-4 h-4" />
                  Change History
                </h4>
                
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[19px] top-4 bottom-4 w-0.5 bg-slate-200" />
                  
                  {/* Events */}
                  <div className="space-y-3">
                    {history.history.map((event, idx) => {
                      const opType = event.operation?.type || 'UPDATE';
                      const colors = HISTORY_OP_COLORS[opType] || HISTORY_OP_COLORS.UPDATE;
                      const OpIcon = HISTORY_OP_ICONS[opType] || FileEdit;
                      const isExpanded = expandedEvents.has(event._id);
                      const hasDiff = event.data?.diff && Object.keys(event.data.diff).length > 0;
                      
                      return (
                        <div key={event._id || idx} className="relative">
                          {/* Timeline dot */}
                          <div className={`absolute left-[11px] w-4 h-4 rounded-full ${colors.icon} ring-4 ring-white flex items-center justify-center`}>
                            <OpIcon className="w-2.5 h-2.5 text-white" />
                          </div>
                          
                          {/* Event Card */}
                          <div className={`ml-10 border rounded-lg ${colors.border} overflow-hidden`}>
                            <div 
                              className={`px-4 py-3 ${colors.bg} cursor-pointer`}
                              onClick={() => toggleEvent(event._id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${colors.text} bg-white/50`}>
                                    {opType}
                                  </span>
                                  <span className="text-sm font-medium text-slate-700">
                                    {event.data?.document_summary || event.operation?.description || 'Change recorded'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <span>{formatTimeAgo(event.timestamp)}</span>
                                  {(hasDiff || event.data?.before || event.data?.after) && (
                                    <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <div className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {event.actor?.username || event.actor?.user_id || 'anonymous'}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Globe className="w-3 h-3" />
                                  {event.trigger?.source || 'API'}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatTimestamp(event.timestamp)}
                                </div>
                              </div>
                            </div>
                            
                            {/* Expanded Diff View */}
                            {isExpanded && (
                              <div className="border-t border-slate-200 bg-white p-4 space-y-3">
                                {/* Trigger Info */}
                                <div className="text-xs">
                                  <span className="text-slate-500">Endpoint:</span>
                                  <code className="ml-2 bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                                    {event.trigger?.http_method} {event.trigger?.endpoint}
                                  </code>
                                </div>
                                
                                {/* Enhanced Side-by-Side Diff View for UPDATE operations */}
                                {opType === 'UPDATE' && (event.data?.before || event.data?.after) && (
                                  <div className="mt-3">
                                    <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                                      <FileEdit className="w-3.5 h-3.5" />
                                      Document Changes (Before → After)
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      {/* Before State */}
                                      <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                        <div className="flex items-center gap-1 mb-2">
                                          <div className="w-2 h-2 rounded-full bg-red-500" />
                                          <span className="text-xs font-semibold text-red-700">Before</span>
                                        </div>
                                        {event.data?.before ? (
                                          <div className="space-y-1 text-xs">
                                            {Object.entries(event.data.before).filter(([k]) => !k.startsWith('_') && k !== 'updated_at').slice(0, 10).map(([key, value]) => {
                                              const afterValue = event.data?.after?.[key];
                                              const isChanged = JSON.stringify(value) !== JSON.stringify(afterValue);
                                              return (
                                                <div key={key} className={`flex gap-2 ${isChanged ? 'bg-red-100 -mx-1 px-1 rounded' : ''}`}>
                                                  <span className="font-mono text-slate-600 min-w-[80px] truncate">{key}:</span>
                                                  <span className={`truncate max-w-[120px] ${isChanged ? 'text-red-700 font-medium line-through' : 'text-slate-500'}`}>
                                                    {typeof value === 'object' ? JSON.stringify(value).slice(0, 30) : String(value).slice(0, 30)}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <span className="text-xs text-slate-400 italic">No before state captured</span>
                                        )}
                                      </div>
                                      
                                      {/* After State */}
                                      <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                                        <div className="flex items-center gap-1 mb-2">
                                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                          <span className="text-xs font-semibold text-emerald-700">After</span>
                                        </div>
                                        {event.data?.after ? (
                                          <div className="space-y-1 text-xs">
                                            {Object.entries(event.data.after).filter(([k]) => !k.startsWith('_') && k !== 'updated_at').slice(0, 10).map(([key, value]) => {
                                              const beforeValue = event.data?.before?.[key];
                                              const isChanged = JSON.stringify(value) !== JSON.stringify(beforeValue);
                                              const isNew = beforeValue === undefined;
                                              return (
                                                <div key={key} className={`flex gap-2 ${isChanged ? 'bg-emerald-100 -mx-1 px-1 rounded' : ''}`}>
                                                  <span className="font-mono text-slate-600 min-w-[80px] truncate">{key}:</span>
                                                  <span className={`truncate max-w-[120px] ${isChanged ? 'text-emerald-700 font-medium' : 'text-slate-500'}`}>
                                                    {typeof value === 'object' ? JSON.stringify(value).slice(0, 30) : String(value).slice(0, 30)}
                                                    {isNew && <span className="ml-1 text-emerald-600">(new)</span>}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <span className="text-xs text-slate-400 italic">No after state captured</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Field-Level Diff Summary */}
                                {hasDiff && (
                                  <div>
                                    <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                                      <Zap className="w-3.5 h-3.5" />
                                      Field-Level Changes
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                                      {Object.entries(event.data.diff).map(([field, change]) => (
                                        <div key={field} className="flex items-start gap-2 text-xs">
                                          <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white ${
                                            change.from === undefined || change.from === null ? 'bg-emerald-500' : 
                                            change.to === undefined || change.to === null ? 'bg-red-500' : 'bg-blue-500'
                                          }`}>
                                            {change.from === undefined || change.from === null ? '+' : 
                                             change.to === undefined || change.to === null ? '-' : '~'}
                                          </span>
                                          <span className="font-mono font-medium text-slate-700 min-w-[100px]">{field}</span>
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              {change.from !== undefined && change.from !== null && (
                                                <span className="text-red-600 bg-red-50 px-2 py-1 rounded line-through max-w-[200px] truncate">
                                                  {JSON.stringify(change.from)}
                                                </span>
                                              )}
                                              {change.from !== undefined && change.to !== undefined && (
                                                <span className="text-slate-400">→</span>
                                              )}
                                              {change.to !== undefined && change.to !== null && (
                                                <span className="text-emerald-600 bg-emerald-50 px-2 py-1 rounded max-w-[200px] truncate">
                                                  {JSON.stringify(change.to)}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* For INSERT operations, show the created document */}
                                {opType === 'INSERT' && event.data?.after && (
                                  <div>
                                    <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                                      <PlusCircle className="w-3.5 h-3.5 text-emerald-600" />
                                      Document Created
                                    </div>
                                    <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 space-y-1 text-xs">
                                      {Object.entries(event.data.after).filter(([k]) => !k.startsWith('_')).slice(0, 8).map(([key, value]) => (
                                        <div key={key} className="flex gap-2">
                                          <span className="font-mono text-slate-600 min-w-[80px]">{key}:</span>
                                          <span className="text-emerald-700 truncate max-w-[300px]">
                                            {typeof value === 'object' ? JSON.stringify(value).slice(0, 50) : String(value).slice(0, 50)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* For DELETE operations, show the deleted document */}
                                {opType === 'DELETE' && event.data?.before && (
                                  <div>
                                    <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                      Document Deleted
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-3 border border-red-200 space-y-1 text-xs">
                                      {Object.entries(event.data.before).filter(([k]) => !k.startsWith('_')).slice(0, 8).map(([key, value]) => (
                                        <div key={key} className="flex gap-2">
                                          <span className="font-mono text-slate-600 min-w-[80px]">{key}:</span>
                                          <span className="text-red-700 line-through truncate max-w-[300px]">
                                            {typeof value === 'object' ? JSON.stringify(value).slice(0, 50) : String(value).slice(0, 50)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Affected Fields */}
                                {event.data?.affected_fields?.length > 0 && !hasDiff && opType === 'UPDATE' && (
                                  <div>
                                    <div className="text-xs font-semibold text-slate-600 mb-1">Fields Affected:</div>
                                    <div className="flex flex-wrap gap-1">
                                      {event.data.affected_fields.map(field => (
                                        <span key={field} className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">
                                          {field}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Trace Link */}
                                <div className="text-xs text-slate-400 pt-2 border-t border-slate-100">
                                  Trace: <code className="font-mono">{event.trace_id?.slice(0, 24)}...</code>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <History className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">No history available</p>
              <p className="text-xs text-slate-400 mt-1">This document has no recorded changes</p>
            </div>
          )}
        </div>
        
        {/* Modal Footer */}
        <div className="border-t border-slate-200 px-6 py-3 bg-slate-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Showing {history?.total_events || 0} change{history?.total_events !== 1 ? 's' : ''} for this document
            </p>
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-sm bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SCHEMA VIEW COMPONENT
// ============================================

const SchemaView = ({ collectionName }) => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadSchema = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/db-explorer/collections/${collectionName}/schema`);
        setSchema(response.data);
      } catch (error) {
        console.error('Failed to load schema:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSchema();
  }, [collectionName]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (!schema) return null;
  
  return (
    <div className="p-4 space-y-6">
      {/* Indexes */}
      {schema.indexes?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <Database className="w-4 h-4" />
            Indexes ({schema.indexes.length})
          </h3>
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            {schema.indexes.map((idx, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="font-mono font-medium text-slate-700">{idx.name}</span>
                {idx.unique && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">unique</span>}
                <span className="text-slate-400">on</span>
                <span className="font-mono text-slate-600">{idx.keys.join(', ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Relationships */}
      {schema.relationships?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Relationships ({schema.relationships.length})
          </h3>
          <div className="bg-rose-50 rounded-lg p-3 space-y-2">
            {schema.relationships.map((rel, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="font-mono font-medium text-rose-700">{rel.field}</span>
                <span className="text-slate-400">→</span>
                <span className="font-mono text-rose-600">{rel.related_collection}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Fields */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <FileJson className="w-4 h-4" />
          Fields ({Object.keys(schema.schema || {}).length})
        </h3>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Field</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Type</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Nullable</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Sample Values</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Object.entries(schema.schema || {}).map(([field, info]) => (
                <tr key={field} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono font-medium text-slate-700">{field}</td>
                  <td className="px-3 py-2">
                    <TypeBadge type={info.primary_type} />
                  </td>
                  <td className="px-3 py-2">
                    {info.is_nullable ? (
                      <span className="text-amber-600">Yes</span>
                    ) : (
                      <span className="text-green-600">No</span>
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-xs truncate text-slate-500">
                    {info.sample_values?.slice(0, 2).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================
// GRAPHS/TIMESERIES VIEW COMPONENT
// ============================================

const TIME_RANGES = [
  { value: 'last_10m', label: 'Last 10 min', granularity: 'minute' },
  { value: 'last_15m', label: 'Last 15 min', granularity: 'minute' },
  { value: 'last_30m', label: 'Last 30 min', granularity: 'minute' },
  { value: 'last_1h', label: 'Last 1 hour', granularity: 'minute' },
  { value: 'last_6h', label: 'Last 6 hours', granularity: 'hour' },
  { value: 'last_24h', label: 'Last 24 hours', granularity: 'hour' },
  { value: 'last_7d', label: 'Last 7 days', granularity: 'day' },
  { value: 'last_30d', label: 'Last 30 days', granularity: 'day' },
  { value: 'last_90d', label: 'Last 90 days', granularity: 'week' },
  { value: 'last_year', label: 'Last year', granularity: 'month' },
  { value: 'all', label: 'All time', granularity: 'month' },
];

const GRANULARITIES = [
  { value: 'minute', label: 'By Minute' },
  { value: 'hour', label: 'By Hour' },
  { value: 'day', label: 'By Day' },
  { value: 'week', label: 'By Week' },
  { value: 'month', label: 'By Month' },
];

const GraphsView = ({ collectionName }) => {
  const [timeseriesData, setTimeseriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeType, setRangeType] = useState('last_24h');
  const [granularity, setGranularity] = useState('hour');
  const [error, setError] = useState(null);
  
  const loadTimeseries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/db-explorer/collections/${collectionName}/timeseries`, {
        params: { range_type: rangeType, granularity }
      });
      if (response.data.error) {
        setError(response.data.error);
      } else {
        setTimeseriesData(response.data);
      }
    } catch (err) {
      console.error('Failed to load timeseries:', err);
      setError('Failed to load time series data');
    } finally {
      setLoading(false);
    }
  }, [collectionName, rangeType, granularity]);
  
  useEffect(() => {
    loadTimeseries();
  }, [loadTimeseries]);
  
  // Handle range change and auto-set appropriate granularity
  const handleRangeChange = (newRange) => {
    setRangeType(newRange);
    const rangeConfig = TIME_RANGES.find(r => r.value === newRange);
    if (rangeConfig) {
      setGranularity(rangeConfig.granularity);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p className="font-medium">Unable to generate time series</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }
  
  const maxCount = Math.max(...(timeseriesData?.data_points?.map(d => d.count) || [1]));
  
  return (
    <div className="p-4 space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-slate-600">Time Range:</span>
          <select
            value={rangeType}
            onChange={(e) => handleRangeChange(e.target.value)}
            className="px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {TIME_RANGES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-slate-600">Granularity:</span>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value)}
            className="px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {GRANULARITIES.map(g => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>
        
        <button
          onClick={loadTimeseries}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <Activity className="w-4 h-4" />
            <span className="text-xs font-medium">Total in Range</span>
          </div>
          <div className="text-2xl font-bold text-blue-700">
            {timeseriesData?.total_in_range?.toLocaleString() || 0}
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3 border border-emerald-200">
          <div className="flex items-center gap-2 text-emerald-600 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-medium">Peak Count</span>
          </div>
          <div className="text-2xl font-bold text-emerald-700">
            {timeseriesData?.peak?.count?.toLocaleString() || 0}
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <Calendar className="w-4 h-4" />
            <span className="text-xs font-medium">Peak Time</span>
          </div>
          <div className="text-sm font-bold text-purple-700 truncate">
            {timeseriesData?.peak?.label || '—'}
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 border border-amber-200">
          <div className="flex items-center gap-2 text-amber-600 mb-1">
            <Database className="w-4 h-4" />
            <span className="text-xs font-medium">Timestamp Field</span>
          </div>
          <div className="text-sm font-bold text-amber-700 font-mono truncate">
            {timeseriesData?.timestamp_field || '_id'}
          </div>
        </div>
      </div>
      
      {/* Chart */}
      {timeseriesData?.data_points?.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Document Creation Timeline
            <span className="text-xs font-normal text-slate-400 ml-2">
              ({timeseriesData.data_points.length} data points)
            </span>
          </h3>
          
          {/* Bar Chart */}
          <div className="relative">
            {/* Y-axis labels - Dynamic based on maxCount */}
            <div className="absolute left-0 top-0 h-[180px] w-12 flex flex-col justify-between text-[10px] text-slate-500 font-medium">
              <span className="text-right pr-2">{maxCount}</span>
              <span className="text-right pr-2">{Math.round(maxCount * 0.75)}</span>
              <span className="text-right pr-2">{Math.round(maxCount * 0.5)}</span>
              <span className="text-right pr-2">{Math.round(maxCount * 0.25)}</span>
              <span className="text-right pr-2">0</span>
            </div>
            
            {/* Chart area */}
            <div className="ml-14 overflow-x-auto">
              <div className="flex items-end gap-1 min-w-max h-[200px] border-l border-b border-slate-200 pl-2 pb-2">
                {timeseriesData.data_points.map((point, idx) => {
                  const heightPx = maxCount > 0 ? Math.max((point.count / maxCount) * 180, 8) : 8;
                  const isPeak = point.count === timeseriesData.peak?.count;
                  
                  return (
                    <div key={idx} className="flex flex-col items-center justify-end group relative h-full">
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                        <div className="bg-slate-800 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap">
                          <div className="font-medium">{point.label}</div>
                          <div className="text-slate-300">{point.count} documents</div>
                        </div>
                      </div>
                      
                      {/* Bar - using fixed pixel height */}
                      <div
                        className={`w-6 md:w-8 rounded-t transition-all duration-200 cursor-pointer ${
                          isPeak 
                            ? 'bg-gradient-to-t from-emerald-500 to-emerald-400' 
                            : 'bg-gradient-to-t from-blue-500 to-blue-400 hover:from-blue-600 hover:to-blue-500'
                        }`}
                        style={{ height: `${heightPx}px` }}
                        title={`${point.label}: ${point.count}`}
                      />
                      
                      {/* X-axis label (show every nth based on data points) */}
                      {(idx % Math.max(1, Math.floor(timeseriesData.data_points.length / 10)) === 0) && (
                        <div className="mt-2 text-[9px] text-slate-500 -rotate-45 origin-top-left whitespace-nowrap w-16 truncate">
                          {point.label}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 mt-6 pt-4 border-t border-slate-100 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-gradient-to-t from-blue-500 to-blue-400" />
              <span className="text-slate-600">Documents</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-gradient-to-t from-emerald-500 to-emerald-400" />
              <span className="text-slate-600">Peak</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
          <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No data points in the selected time range</p>
          <p className="text-xs text-slate-400 mt-1">Try selecting a different time range or granularity</p>
        </div>
      )}
      
      {/* Data Table */}
      {timeseriesData?.data_points?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-700">Raw Data</h3>
          </div>
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Time Period</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">Count</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Visual</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {timeseriesData.data_points.map((point, idx) => {
                  const width = maxCount > 0 ? (point.count / maxCount) * 100 : 0;
                  const isPeak = point.count === timeseriesData.peak?.count;
                  
                  return (
                    <tr key={idx} className={`hover:bg-slate-50 ${isPeak ? 'bg-emerald-50' : ''}`}>
                      <td className="px-3 py-2 font-mono text-slate-700">{point.label}</td>
                      <td className="px-3 py-2 text-right font-medium text-slate-800">{point.count.toLocaleString()}</td>
                      <td className="px-3 py-2 w-1/2">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${isPeak ? 'bg-emerald-500' : 'bg-blue-500'}`}
                            style={{ width: `${width}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// PROVENANCE VIEW COMPONENT
// ============================================

const OPERATION_ICONS = {
  INSERT: PlusCircle,
  UPDATE: FileEdit,
  DELETE: Trash2,
  BULK_INSERT: Package,
};

const OPERATION_COLORS = {
  INSERT: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  UPDATE: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  DELETE: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  BULK_INSERT: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
};

const COLLECTION_ICONS = {
  parts: Package,
  part_oem_refs: Link2,
  manufacturers: Layers,
  audit_events_v2: FileText,
  taxonomy_subgroups: ListTree,
  taxonomy_main_groups: ListTree,
  provenance_events: GitBranch,
};

const ProvenanceView = ({ collectionName }) => {
  const [provenanceData, setProvenanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [traceDetails, setTraceDetails] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [viewType, setViewType] = useState('timeline'); // 'timeline' | 'traces' | 'stats'

  useEffect(() => {
    loadProvenanceData();
    loadStats();
  }, [collectionName]);

  const loadProvenanceData = async () => {
    try {
      setLoading(true);
      // Get timeline filtered by collection if selected
      const params = collectionName ? { collection: collectionName, limit: 50 } : { limit: 50 };
      const response = await api.get('/api/v1/provenance/timeline', { params });
      setProvenanceData(response.data);
    } catch (error) {
      console.error('Failed to load provenance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/api/v1/provenance/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load provenance stats:', error);
    }
  };

  const loadTraceDetails = async (traceId) => {
    try {
      setTraceLoading(true);
      const response = await api.get(`/api/v1/provenance/trace/${traceId}`);
      setTraceDetails(response.data);
      setSelectedTrace(traceId);
    } catch (error) {
      console.error('Failed to load trace details:', error);
    } finally {
      setTraceLoading(false);
    }
  };

  // Use IST timezone utilities
  const formatTimestamp = (ts) => formatIST(ts, { includeSeconds: true });
  const formatTimeAgo = (ts) => formatTimeAgoIST(ts);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-sm text-slate-500">Loading provenance data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-4 p-4 overflow-auto">
      {/* Header with view toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <GitBranch className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Data Provenance</h3>
            <p className="text-xs text-slate-500">
              {collectionName ? `Events for ${collectionName}` : 'All events across collections'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewType('timeline')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewType === 'timeline' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setViewType('traces')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewType === 'traces' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Traces
            </button>
            <button
              onClick={() => setViewType('stats')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewType === 'stats' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Stats
            </button>
          </div>
          
          <button
            onClick={loadProvenanceData}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Summary Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-3 border border-indigo-200">
            <div className="flex items-center gap-2 text-indigo-600 mb-1">
              <Activity className="w-4 h-4" />
              <span className="text-xs font-medium">Total Events</span>
            </div>
            <div className="text-2xl font-bold text-indigo-700">
              {stats.total_events?.toLocaleString() || 0}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3 border border-emerald-200">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <GitBranch className="w-4 h-4" />
              <span className="text-xs font-medium">Unique Traces</span>
            </div>
            <div className="text-2xl font-bold text-emerald-700">
              {stats.unique_traces?.toLocaleString() || 0}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">Last 24h</span>
            </div>
            <div className="text-2xl font-bold text-blue-700">
              {stats.events_last_24h?.toLocaleString() || 0}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs font-medium">Last 7 Days</span>
            </div>
            <div className="text-2xl font-bold text-purple-700">
              {stats.events_last_7d?.toLocaleString() || 0}
            </div>
          </div>
        </div>
      )}

      {/* Main Content based on viewType */}
      {viewType === 'timeline' && (
        <div className="flex gap-4">
          {/* Timeline List */}
          <div className={`flex-1 bg-white border border-slate-200 rounded-lg overflow-hidden ${selectedTrace ? 'max-w-md' : ''}`}>
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Recent Events
                <span className="text-xs font-normal text-slate-400">
                  ({provenanceData?.total || 0} total)
                </span>
              </h4>
            </div>
            
            <div className="divide-y divide-slate-100 max-h-[500px] overflow-auto">
              {provenanceData?.events?.length > 0 ? (
                provenanceData.events.map((event, idx) => {
                  const opColors = OPERATION_COLORS[event.operation?.type] || OPERATION_COLORS.INSERT;
                  const OpIcon = OPERATION_ICONS[event.operation?.type] || PlusCircle;
                  const CollIcon = COLLECTION_ICONS[event.operation?.collection] || Database;
                  const isSelected = selectedTrace === event.trace_id;
                  
                  return (
                    <div 
                      key={event._id || idx}
                      onClick={() => loadTraceDetails(event.trace_id)}
                      className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                        isSelected ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Operation Icon */}
                        <div className={`p-1.5 rounded-lg ${opColors.bg} ${opColors.border} border`}>
                          <OpIcon className={`w-4 h-4 ${opColors.text}`} />
                        </div>
                        
                        {/* Event Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${opColors.bg} ${opColors.text}`}>
                              {event.operation?.type}
                            </span>
                            <span className="text-xs text-slate-500">on</span>
                            <div className="flex items-center gap-1">
                              <CollIcon className="w-3 h-3 text-slate-400" />
                              <span className="text-xs font-medium text-slate-700">{event.operation?.collection}</span>
                            </div>
                          </div>
                          
                          <p className="text-sm text-slate-600 truncate">
                            {event.data?.document_summary || event.operation?.document_id?.slice(0, 12) + '...'}
                          </p>
                          
                          <div className="flex items-center gap-3 mt-1.5">
                            <div className="flex items-center gap-1 text-[10px] text-slate-400">
                              <User className="w-3 h-3" />
                              {event.actor?.username || event.actor?.user_id || 'anonymous'}
                            </div>
                            <div className="flex items-center gap-1 text-[10px] text-slate-400">
                              <Clock className="w-3 h-3" />
                              {formatTimeAgo(event.timestamp)}
                            </div>
                            <div className="flex items-center gap-1 text-[10px] text-slate-400">
                              <Globe className="w-3 h-3" />
                              {event.trigger?.source || 'API'}
                            </div>
                          </div>
                        </div>
                        
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-slate-500">
                  <GitBranch className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No provenance events found</p>
                  <p className="text-xs text-slate-400 mt-1">Create or modify data to see events</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Trace Details Panel */}
          {selectedTrace && (
            <div className="flex-1 bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-indigo-800 flex items-center gap-2">
                  <GitBranch className="w-4 h-4" />
                  Trace Details
                </h4>
                <button 
                  onClick={() => { setSelectedTrace(null); setTraceDetails(null); }}
                  className="p-1 hover:bg-indigo-100 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-indigo-600" />
                </button>
              </div>
              
              {traceLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                </div>
              ) : traceDetails ? (
                <div className="p-4 space-y-4 max-h-[500px] overflow-auto">
                  {/* Trace Summary */}
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Trace ID:</span>
                      <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                        {traceDetails.trace_id?.slice(0, 20)}...
                      </code>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-slate-500">Actor:</span>
                        <span className="ml-1 font-medium text-slate-700">
                          {traceDetails.actor?.username || traceDetails.actor?.user_id || 'anonymous'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Source:</span>
                        <span className="ml-1 font-medium text-slate-700">
                          {traceDetails.trigger?.source || 'API'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Endpoint:</span>
                        <span className="ml-1 font-mono text-slate-700">
                          {traceDetails.trigger?.endpoint || '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Started:</span>
                        <span className="ml-1 font-medium text-slate-700">
                          {formatTimestamp(traceDetails.started_at)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Summary */}
                    <div className="flex items-center gap-4 pt-2 border-t border-slate-200">
                      <div className="flex items-center gap-1">
                        <span className="text-emerald-600 font-bold">{traceDetails.summary?.inserts || 0}</span>
                        <span className="text-xs text-slate-500">inserts</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-blue-600 font-bold">{traceDetails.summary?.updates || 0}</span>
                        <span className="text-xs text-slate-500">updates</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-red-600 font-bold">{traceDetails.summary?.deletes || 0}</span>
                        <span className="text-xs text-slate-500">deletes</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Collections Affected */}
                  <div>
                    <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Collections Affected</h5>
                    <div className="flex flex-wrap gap-1">
                      {traceDetails.summary?.collections_affected?.map(coll => {
                        const Icon = COLLECTION_ICONS[coll] || Database;
                        return (
                          <span key={coll} className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                            <Icon className="w-3 h-3" />
                            {coll}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Events Timeline */}
                  <div>
                    <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Operations ({traceDetails.events?.length || 0})</h5>
                    <div className="space-y-2">
                      {traceDetails.events?.map((event, idx) => {
                        const opColors = OPERATION_COLORS[event.operation?.type] || OPERATION_COLORS.INSERT;
                        const OpIcon = OPERATION_ICONS[event.operation?.type] || PlusCircle;
                        const CollIcon = COLLECTION_ICONS[event.operation?.collection] || Database;
                        
                        return (
                          <div key={idx} className="flex items-start gap-2">
                            {/* Sequence Number */}
                            <div className="w-5 h-5 flex items-center justify-center bg-slate-200 rounded-full text-[10px] font-bold text-slate-600">
                              {event.sequence}
                            </div>
                            
                            {/* Event Card */}
                            <div className={`flex-1 border rounded-lg p-2 ${opColors.border} ${opColors.bg}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <OpIcon className={`w-3.5 h-3.5 ${opColors.text}`} />
                                <span className={`text-xs font-semibold ${opColors.text}`}>
                                  {event.operation?.type}
                                </span>
                                <span className="text-xs text-slate-400">→</span>
                                <CollIcon className="w-3 h-3 text-slate-500" />
                                <span className="text-xs font-medium text-slate-600">
                                  {event.operation?.collection}
                                </span>
                              </div>
                              
                              <p className="text-xs text-slate-600 truncate">
                                {event.data?.document_summary || event.operation?.document_id}
                              </p>
                              
                              {/* Diff for updates */}
                              {event.operation?.type === 'UPDATE' && event.data?.diff && Object.keys(event.data.diff).length > 0 && (
                                <div className="mt-2 pt-2 border-t border-slate-200">
                                  <span className="text-[10px] font-semibold text-slate-500">Changes:</span>
                                  <div className="mt-1 space-y-0.5">
                                    {Object.entries(event.data.diff).slice(0, 3).map(([field, change]) => (
                                      <div key={field} className="text-[10px] font-mono">
                                        <span className="text-slate-500">{field}:</span>
                                        <span className="text-red-500 line-through ml-1">
                                          {JSON.stringify(change.from)?.slice(0, 15)}
                                        </span>
                                        <span className="text-slate-400 mx-1">→</span>
                                        <span className="text-emerald-600">
                                          {JSON.stringify(change.to)?.slice(0, 15)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Stats View */}
      {viewType === 'stats' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Events by Collection */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Events by Collection
              </h4>
            </div>
            <div className="p-4 space-y-2 max-h-64 overflow-auto">
              {Object.entries(stats.events_by_collection || {}).map(([collection, count]) => {
                const Icon = COLLECTION_ICONS[collection] || Database;
                const maxCount = Math.max(...Object.values(stats.events_by_collection));
                const width = (count / maxCount) * 100;
                
                return (
                  <div key={collection} className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-xs text-slate-600 w-32 truncate">{collection}</span>
                    <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-indigo-500 rounded-full"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-12 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Events by Operation */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Events by Operation
              </h4>
            </div>
            <div className="p-4 space-y-2">
              {Object.entries(stats.events_by_operation || {}).map(([operation, count]) => {
                const colors = OPERATION_COLORS[operation] || OPERATION_COLORS.INSERT;
                const Icon = OPERATION_ICONS[operation] || PlusCircle;
                const maxCount = Math.max(...Object.values(stats.events_by_operation));
                const width = (count / maxCount) * 100;
                
                return (
                  <div key={operation} className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${colors.text} flex-shrink-0`} />
                    <span className="text-xs text-slate-600 w-24">{operation}</span>
                    <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${operation === 'INSERT' ? 'bg-emerald-500' : operation === 'UPDATE' ? 'bg-blue-500' : operation === 'DELETE' ? 'bg-red-500' : 'bg-purple-500'}`}
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-12 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Events by Actor */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden md:col-span-2">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                Events by Actor
              </h4>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.events_by_actor || {}).map(([actor, count]) => (
                  <div key={actor} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">{actor || 'anonymous'}</span>
                    <span className="text-xs bg-slate-200 px-1.5 py-0.5 rounded text-slate-600">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Traces View - Group events by trace_id */}
      {viewType === 'traces' && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              Recent Traces (User Actions)
            </h4>
          </div>
          
          <div className="divide-y divide-slate-100 max-h-[500px] overflow-auto">
            {provenanceData?.events?.length > 0 ? (
              // Group by trace_id and show unique traces
              [...new Map(provenanceData.events.map(e => [e.trace_id, e])).values()].map((event, idx) => (
                <div 
                  key={event.trace_id}
                  onClick={() => loadTraceDetails(event.trace_id)}
                  className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-50 rounded-lg">
                        <MousePointerClick className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">
                            {event.trigger?.http_method} {event.trigger?.endpoint}
                          </span>
                          <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">
                            {event.trigger?.source || 'API'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {event.actor?.username || 'anonymous'}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(event.timestamp)}
                          </div>
                          <code className="font-mono text-slate-400 text-[10px]">
                            {event.trace_id?.slice(0, 8)}...
                          </code>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-slate-500">
                <GitBranch className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>No traces found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const DatabaseExplorerPage = () => {
  const [collections, setCollections] = useState([]);
  const [categorized, setCategorized] = useState({});
  const [summary, setSummary] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState(null);
  const [fieldStats, setFieldStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    core_data: true,
    taxonomy: true,
    ai_data: false,
    audit_logs: false,
    system: false
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [historyDocument, setHistoryDocument] = useState(null); // Document for history modal
  const [viewMode, setViewMode] = useState('data'); // 'data' | 'schema' | 'graphs' | 'provenance'
  const [sortBy, setSortBy] = useState('_id');
  const [sortOrder, setSortOrder] = useState(-1);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [filterField, setFilterField] = useState(null);
  const [filterValue, setFilterValue] = useState('');

  useEffect(() => {
    loadCollections();
    loadSummary();
  }, []);

  useEffect(() => {
    if (collectionData?.documents?.[0]) {
      const allKeys = Object.keys(collectionData.documents[0]);
      // Priority columns first
      const priorityKeys = ['_id', 'name', 'code', 'status', 'type', 'created_at', 'updated_at'];
      const otherKeys = allKeys.filter(k => !priorityKeys.includes(k));
      const sorted = [...priorityKeys.filter(k => allKeys.includes(k)), ...otherKeys];
      setVisibleColumns(sorted.slice(0, 10));
    }
  }, [collectionData]);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/db-explorer/collections');
      setCollections(response.data.collections || []);
      setCategorized(response.data.categorized || {});
    } catch (error) {
      console.error('Failed to load collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await api.get('/api/v1/db-explorer/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary:', error);
    }
  };

  const loadCollectionData = useCallback(async (collectionName, skip = 0, sortField = sortBy, sortDir = sortOrder) => {
    try {
      setDataLoading(true);
      const params = { 
        limit: pageSize, 
        skip,
        sort_by: sortField,
        sort_order: sortDir
      };
      
      if (filterField && filterValue) {
        params.filter_field = filterField;
        params.filter_value = filterValue;
      }
      
      const [dataResponse, statsResponse] = await Promise.all([
        api.get(`/api/v1/db-explorer/collections/${collectionName}`, { params }),
        api.get(`/api/v1/db-explorer/collections/${collectionName}/stats`)
      ]);
      
      setCollectionData(dataResponse.data);
      setFieldStats(statsResponse.data.field_stats || {});
      setPage(Math.floor(skip / pageSize));
    } catch (error) {
      console.error('Failed to load collection data:', error);
    } finally {
      setDataLoading(false);
    }
  }, [pageSize, sortBy, sortOrder, filterField, filterValue]);

  const handleCollectionClick = (collectionName) => {
    setSelectedCollection(collectionName);
    setPage(0);
    setSortBy('_id');
    setSortOrder(-1);
    setSelectedRows(new Set());
    setFilterField(null);
    setFilterValue('');
    setViewMode('data');
    loadCollectionData(collectionName, 0, '_id', -1);
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field ? -sortOrder : -1;
    setSortBy(field);
    setSortOrder(newOrder);
    loadCollectionData(selectedCollection, 0, field, newOrder);
  };

  const handleFilter = (field) => {
    if (filterField === field) {
      setFilterField(null);
      setFilterValue('');
      loadCollectionData(selectedCollection, 0);
    } else {
      setFilterField(field);
    }
  };

  const applyFilter = () => {
    loadCollectionData(selectedCollection, 0);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleRowSelection = (idx) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  const toggleAllRows = () => {
    if (selectedRows.size === filteredData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredData.map((_, i) => i)));
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      core_data: <Package className="w-4 h-4" />,
      taxonomy: <Layers className="w-4 h-4" />,
      ai_data: <Cpu className="w-4 h-4" />,
      audit_logs: <FileText className="w-4 h-4" />,
      system: <Settings className="w-4 h-4" />
    };
    return icons[category] || <Table className="w-4 h-4" />;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      core_data: 'Core Data',
      taxonomy: 'Taxonomy & Allocation',
      ai_data: 'AI Generated Data',
      audit_logs: 'Audit Logs',
      system: 'System'
    };
    return labels[category] || category;
  };

  const filteredData = collectionData?.documents || [];
  const totalPages = Math.ceil((collectionData?.total || 0) / pageSize);
  const allColumns = collectionData?.documents?.[0] ? Object.keys(collectionData.documents[0]) : [];

  const goToPage = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      loadCollectionData(selectedCollection, newPage * pageSize);
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-sm text-slate-500">Loading collections...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-bold text-slate-800">Database Explorer</h1>
            <span className="text-sm text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
              {collections.length} collections
            </span>
          </div>
          <button
            onClick={() => { loadCollections(); loadSummary(); if (selectedCollection) loadCollectionData(selectedCollection, page * pageSize); }}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className={`flex-shrink-0 bg-white border-r border-slate-200 flex flex-col transition-all duration-200 ${
          sidebarCollapsed ? 'w-12' : 'w-64'
        }`}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="flex-shrink-0 p-2 border-b border-slate-100 hover:bg-slate-50 transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5 text-slate-400 mx-auto" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {!sidebarCollapsed && (
            <>
              {/* Quick Stats */}
              {summary && (
                <div className="flex-shrink-0 p-3 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white">
                  <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Quick Stats</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Parts', value: summary.data_summary?.parts?.total || 0, color: 'text-blue-600' },
                      { label: 'Mfrs', value: summary.data_summary?.manufacturers?.in_catalog || 0, color: 'text-emerald-600' },
                      { label: 'Groups', value: summary.data_summary?.taxonomy?.main_groups || 0, color: 'text-purple-600' },
                      { label: 'Subs', value: summary.data_summary?.taxonomy?.subgroups || 0, color: 'text-amber-600' },
                    ].map(stat => (
                      <div key={stat.label} className="bg-white rounded-lg p-2 border border-slate-100">
                        <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
                        <div className="text-[10px] text-slate-400">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Collections List */}
              <div className="flex-1 overflow-y-auto p-2">
                {Object.entries(categorized).map(([category, cols]) => (
                  <div key={category} className="mb-1">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                    >
                      {expandedCategories[category] ? (
                        <ChevronDown className="w-3 h-3 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                      )}
                      {getCategoryIcon(category)}
                      <span className="flex-1 text-left truncate">{getCategoryLabel(category)}</span>
                      <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{cols.length}</span>
                    </button>
                    
                    {expandedCategories[category] && (
                      <div className="ml-5 mt-0.5 space-y-0.5">
                        {cols.map(col => (
                          <button
                            key={col.name}
                            onClick={() => handleCollectionClick(col.name)}
                            className={`w-full flex items-center justify-between px-2 py-1 text-xs rounded-md transition-colors ${
                              selectedCollection === col.name
                                ? 'bg-blue-500 text-white'
                                : 'text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <span className="truncate">{col.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              selectedCollection === col.name
                                ? 'bg-blue-600 text-blue-100'
                                : 'bg-slate-200 text-slate-600'
                            }`}>
                              {col.count}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedCollection ? (
            <>
              {/* Collection Header */}
              <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">{selectedCollection}</h2>
                      <p className="text-xs text-slate-500">
                        {collectionData?.total || 0} documents • Page {page + 1} of {totalPages || 1}
                        {selectedRows.size > 0 && ` • ${selectedRows.size} selected`}
                      </p>
                    </div>
                    
                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                      <button
                        onClick={() => setViewMode('data')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          viewMode === 'data' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Data
                      </button>
                      <button
                        onClick={() => setViewMode('schema')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          viewMode === 'schema' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Schema
                      </button>
                      <button
                        onClick={() => setViewMode('graphs')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                          viewMode === 'graphs' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <BarChart3 className="w-3 h-3" />
                        Graphs
                      </button>
                      <button
                        onClick={() => setViewMode('provenance')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                          viewMode === 'provenance' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <GitBranch className="w-3 h-3" />
                        Provenance
                      </button>
                      <button
                        onClick={() => setViewMode('backups')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                          viewMode === 'backups' ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <HardDrive className="w-3 h-3" />
                        Backups
                      </button>
                    </div>
                  </div>
                  
                  {viewMode === 'data' && (
                    <div className="flex items-center gap-3">
                      {/* Filter Indicator */}
                      {filterField && (
                        <div className="flex items-center gap-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded-lg">
                          <Filter className="w-3 h-3 text-amber-600" />
                          <span className="text-xs text-amber-700">{filterField}: {filterValue}</span>
                          <button onClick={() => { setFilterField(null); setFilterValue(''); loadCollectionData(selectedCollection, 0); }}>
                            <X className="w-3 h-3 text-amber-600 hover:text-amber-800" />
                          </button>
                        </div>
                      )}

                      {/* Column Selector */}
                      <div className="relative group">
                        <button className="flex items-center gap-1 px-2 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                          <Columns className="w-3.5 h-3.5" />
                          <span>{visibleColumns.length}/{allColumns.length}</span>
                        </button>
                        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-20 hidden group-hover:block w-48 max-h-64 overflow-auto">
                          <div className="text-[10px] text-slate-400 uppercase font-semibold mb-2">Visible Columns</div>
                          {allColumns.map(col => (
                            <label key={col} className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-slate-50 px-1 rounded">
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes(col)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setVisibleColumns([...visibleColumns, col]);
                                  } else {
                                    setVisibleColumns(visibleColumns.filter(c => c !== col));
                                  }
                                }}
                                className="rounded border-slate-300"
                              />
                              <span className="truncate">{col}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Page Size */}
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const newSize = Number(e.target.value);
                          setPageSize(newSize);
                          loadCollectionData(selectedCollection, 0);
                        }}
                        className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={10}>10 / page</option>
                        <option value={15}>15 / page</option>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-hidden flex flex-col">
                {viewMode === 'schema' ? (
                  <div className="flex-1 overflow-auto">
                    <SchemaView collectionName={selectedCollection} />
                  </div>
                ) : viewMode === 'graphs' ? (
                  <div className="flex-1 overflow-auto">
                    <GraphsView collectionName={selectedCollection} />
                  </div>
                ) : viewMode === 'provenance' ? (
                  <div className="flex-1 overflow-auto">
                    <ProvenanceView collectionName={selectedCollection} />
                  </div>
                ) : viewMode === 'backups' ? (
                  <div className="flex-1 overflow-auto">
                    <BackupsView />
                  </div>
                ) : dataLoading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : filteredData.length > 0 ? (
                  <>
                    {/* Data Table */}
                    <div className="flex-1 overflow-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead className="bg-slate-50 sticky top-0 z-10">
                          <tr>
                            {/* Selection Checkbox */}
                            <th className="px-2 py-2 border-b border-slate-200 w-8">
                              <button onClick={toggleAllRows} className="p-0.5 hover:bg-slate-200 rounded">
                                {selectedRows.size === filteredData.length ? (
                                  <CheckSquare className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <Square className="w-4 h-4 text-slate-400" />
                                )}
                              </button>
                            </th>
                            {/* Row Number */}
                            <th className="px-2 py-2 border-b border-slate-200 text-left font-semibold text-slate-500 w-10">#</th>
                            {/* Data Columns */}
                            {visibleColumns.map(key => {
                              const stats = fieldStats?.[key];
                              const isSorted = sortBy === key;
                              
                              return (
                                <th 
                                  key={key}
                                  className="px-3 py-2 border-b border-slate-200 text-left relative group"
                                  onMouseEnter={() => setHoveredColumn(key)}
                                  onMouseLeave={() => setHoveredColumn(null)}
                                >
                                  <div className="flex items-center gap-1">
                                    {/* Type indicator */}
                                    {stats && <TypeBadge type={stats.type} small />}
                                    
                                    {/* Column name & sort */}
                                    <button 
                                      onClick={() => handleSort(key)}
                                      className="flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-800 uppercase tracking-wider"
                                    >
                                      {key.replace(/_/g, ' ')}
                                      {isSorted ? (
                                        sortOrder > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                                      ) : (
                                        <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                                      )}
                                    </button>
                                    
                                    {/* Filter button */}
                                    <button
                                      onClick={() => handleFilter(key)}
                                      className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                                        filterField === key ? 'opacity-100 bg-amber-100' : 'hover:bg-slate-200'
                                      }`}
                                    >
                                      <Filter className={`w-3 h-3 ${filterField === key ? 'text-amber-600' : 'text-slate-400'}`} />
                                    </button>
                                  </div>
                                  
                                  {/* Stats Tooltip */}
                                  {hoveredColumn === key && stats && (
                                    <FieldStatsTooltip stats={stats} fieldName={key} />
                                  )}
                                  
                                  {/* Filter Input */}
                                  {filterField === key && (
                                    <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-30 w-48">
                                      <input
                                        type="text"
                                        value={filterValue}
                                        onChange={(e) => setFilterValue(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
                                        placeholder={`Filter ${key}...`}
                                        className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        autoFocus
                                      />
                                      <div className="flex gap-1 mt-2">
                                        <button
                                          onClick={applyFilter}
                                          className="flex-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                          Apply
                                        </button>
                                        <button
                                          onClick={() => { setFilterField(null); setFilterValue(''); }}
                                          className="px-2 py-1 text-xs bg-slate-100 rounded hover:bg-slate-200"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </th>
                              );
                            })}
                            {/* Actions Column */}
                            <th className="px-2 py-2 border-b border-slate-200 w-20">
                              <span className="text-[10px] uppercase text-slate-400">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-100">
                          {filteredData.map((doc, idx) => (
                            <tr 
                              key={idx} 
                              className={`hover:bg-blue-50/50 transition-colors cursor-pointer ${
                                selectedRows.has(idx) ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => setSelectedDocument(doc)}
                            >
                              {/* Selection */}
                              <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                <button onClick={() => toggleRowSelection(idx)} className="p-0.5">
                                  {selectedRows.has(idx) ? (
                                    <CheckSquare className="w-4 h-4 text-blue-600" />
                                  ) : (
                                    <Square className="w-4 h-4 text-slate-300 hover:text-slate-400" />
                                  )}
                                </button>
                              </td>
                              {/* Row Number */}
                              <td className="px-2 py-2 text-slate-400 font-mono text-[10px]">
                                {page * pageSize + idx + 1}
                              </td>
                              {/* Data Cells */}
                              {visibleColumns.map(key => (
                                <td key={key} className="px-3 py-2 max-w-[200px]">
                                  <CellValue 
                                    value={doc[key]}
                                    fieldName={key}
                                    onClickId={(id) => {
                                      // Could navigate to related document
                                      console.log('Navigate to:', id);
                                    }}
                                  />
                                </td>
                              ))}
                              {/* Action Buttons */}
                              <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1">
                                  {/* View JSON */}
                                  <button
                                    onClick={() => setSelectedDocument(doc)}
                                    className="p-1 hover:bg-slate-100 rounded transition-colors"
                                    title="View document"
                                  >
                                    <Eye className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                                  </button>
                                  {/* View History */}
                                  <button
                                    onClick={() => setHistoryDocument(doc)}
                                    className="p-1 hover:bg-indigo-50 rounded transition-colors"
                                    title="View change history"
                                  >
                                    <History className="w-3.5 h-3.5 text-indigo-400 hover:text-indigo-600" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="flex-shrink-0 bg-white border-t border-slate-200 px-4 py-2 flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, collectionData?.total || 0)} of {collectionData?.total || 0}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => goToPage(0)}
                          disabled={page === 0}
                          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => goToPage(page - 1)}
                          disabled={page === 0}
                          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        
                        {/* Page Numbers */}
                        <div className="flex items-center gap-1 mx-2">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i;
                            } else if (page < 3) {
                              pageNum = i;
                            } else if (page > totalPages - 4) {
                              pageNum = totalPages - 5 + i;
                            } else {
                              pageNum = page - 2 + i;
                            }
                            return (
                              <button
                                key={pageNum}
                                onClick={() => goToPage(pageNum)}
                                className={`w-7 h-7 text-xs rounded transition-colors ${
                                  page === pageNum
                                    ? 'bg-blue-500 text-white'
                                    : 'hover:bg-slate-100'
                                }`}
                              >
                                {pageNum + 1}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          onClick={() => goToPage(page + 1)}
                          disabled={page >= totalPages - 1}
                          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => goToPage(totalPages - 1)}
                          disabled={page >= totalPages - 1}
                          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronsRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <Table className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p>No documents found</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500">
              <Database className="w-16 h-16 mb-4 text-slate-200" />
              <p className="text-lg font-medium">Select a collection</p>
              <p className="text-sm mt-1 text-slate-400">Click on a collection in the sidebar to view its data</p>
            </div>
          )}
        </div>
      </div>

      {/* Document Modal */}
      {selectedDocument && (
        <DocumentModal
          document={selectedDocument}
          collectionName={selectedCollection}
          onClose={() => setSelectedDocument(null)}
        />
      )}

      {/* Document History Modal */}
      {historyDocument && (
        <DocumentHistoryModal
          document={historyDocument}
          collectionName={selectedCollection}
          onClose={() => setHistoryDocument(null)}
        />
      )}
    </div>
  );
};

export default DatabaseExplorerPage;
